// src/Pages/Blogi/Blogi.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { fetchPosts, fetchCategories, extractFeatured } from "../../services/wpApi";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import ImageWithFallback from "../../components/Image/ImageWithFallback.jsx";
import BlogSidebar from "../../components/Blog/Sidebar/BlogSidebar.jsx";
import { useTheme } from "../../context/ThemeContext";
import { motion, useScroll, useTransform } from "framer-motion";
import Spinner, { PageOverlayLoading } from "../../components/Loading/Spinner.jsx"; // [SPINNER]

// OG-fallbackkuva (pidä synkassa WP:n BILEBOXI_OG_DEFAULTin kanssa)
const OG_FALLBACK = "https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg";

// Pieni apuri: stripataan HTML tagit (excerptille)
function stripHtml(html = "") {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

export default function Blogi() {
  // URL-paramit: ?page=1&category=ID&search=termi
  const [params, setParams] = useSearchParams();
  const initialPage = Number(params.get("page") || 1);
  const initialCat = params.get("category") || "";
  const initialSearch = params.get("search") || "";

  const { theme } = useTheme();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, 60]);

  // Hero-sävytys
  const heroOverlay =
    theme === "dark"
      ? "from-electric/30 via-black/40 to-black/60"
      : "from-black/40 via-black/60 to-black/75";

  const panelTone =
    theme === "dark"
      ? "bg-black/50 ring-white/5"
      : "bg-lemon/60 ring-black/10";

  // Tilat
  const [page, setPage] = useState(initialPage);
  const [categoryId, setCategoryId] = useState(initialCat);
  const [search, setSearch] = useState(initialSearch);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(true);
  const [error, setError] = useState("");

  // [SPINNER] Ensimmäisen renderin “valmis” -flägit:
  const [catsReady, setCatsReady] = useState(false);
  const [postsReadyOnce, setPostsReadyOnce] = useState(false);
  const firstLoad = !catsReady || !postsReadyOnce;

  // Käytä ensimmäisen artikkelin kuvitusta hero-taustana (jos saatavilla)
  const heroFeat = useMemo(() => {
    const p = Array.isArray(posts) && posts.length ? posts[0] : null;
    return p ? extractFeatured(p) : null;
  }, [posts]);

  // Uusin artikkeli hero-nostoon (sama kuin posts[0], mutta erikseen selkeyden vuoksi)
  const latestPost = useMemo(
    () => (Array.isArray(posts) && posts.length ? posts[0] : null),
    [posts]
  );

  // Hae kategoriat kerran
  useEffect(() => {
    let cancelled = false;
    setFilterLoading(true);
    fetchCategories()
      .then((list) => !cancelled && setCats(list))
      .catch(() => !cancelled && setCats([]))
      .finally(() => {
        if (!cancelled) {
          setFilterLoading(false);
          setCatsReady(true); // [SPINNER]
        }
      });
    return () => (cancelled = true);
  }, []);

  // Hae postit kun page/category/search muuttuu
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    fetchPosts({
      page,
      perPage: 9,
      categoryId: categoryId ? Number(categoryId) : undefined,
      search: search || undefined,
    })
      .then(({ posts, totalPages }) => {
        if (cancelled) return;
        setPosts(posts);
        setTotalPages(totalPages);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setPostsReadyOnce(true); // [SPINNER]
        }
      });

    // Synkkaa URL
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (page > 1) next.set("page", String(page));
      else next.delete("page");

      if (categoryId) next.set("category", String(categoryId));
      else next.delete("category");

      if (search) next.set("search", search);
      else next.delete("search");

      return next;
    });

    return () => (cancelled = true);
  }, [page, categoryId, search, setParams]);

  // ---- Meta (Helmet) – hookit ensin, EI early returnia ennen näitä! ----
  const title = useMemo(() => {
    const base = "Buukkerin blogi";
    if (search) return `${base} – haku: “${search}” | Buukkeri`;
    if (categoryId) {
      const c = cats.find((c) => String(c.id) === String(categoryId));
      if (c) return `${base} – kategoria: ${c.name} | Buukkeri`;
    }
    return `${base} – vinkit esiintyjän buukkaukseen | Buukkeri`;
  }, [search, categoryId, cats]);

  const description = useMemo(() => {
    if (search) return `Blogihaun tulokset hakusanalla “${search}”.`;
    if (categoryId) {
      const c = cats.find((c) => String(c.id) === String(categoryId));
      if (c) return `Buukkerin blogi – kategoria: ${c.name}.`;
    }
    return "Buukkerin blogi: vinkkejä juhliin, esiintyjien tarinoita ja tapahtumajuttuja.";
  }, [search, categoryId, cats]);

  const canonical = useMemo(() => {
    const base =
      typeof window !== "undefined" ? `${window.location.origin}/blogi/` : "/blogi/";
    const qp = new URLSearchParams();
    if (categoryId) qp.set("category", String(categoryId));
    if (search) qp.set("search", search);
    const q = qp.toString();
    return q ? `${base}?${q}` : base;
  }, [categoryId, search]);

  const ogImage = heroFeat?.url || OG_FALLBACK;

  const helmetCb = () => {
    try {
      window.dispatchEvent(new Event("bbx:title"));
    } catch {}
  };

  // ---- Early return VAIN ensimmäisellä latauksella (kokoruudun overlay-spinner) ----
  if (firstLoad) {
    return (
      <>
        <Helmet onChangeClientState={helmetCb}>
          <title>{title}</title>
          <meta name="description" content={description} />
          <link rel="canonical" href={canonical} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={canonical} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:secure_url" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        {/* Kokoruudun overlay-spinneri blogin ensilataukselle */}
        <PageOverlayLoading />
      </>
    );
  }

  if (error) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-16">
        <Helmet onChangeClientState={helmetCb}>
          <title>{title}</title>
          <meta name="description" content={description} />
          <link rel="canonical" href={canonical} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={canonical} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:secure_url" content={ogImage} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        <h1 className="text-3xl font-bold mb-6">Blogi</h1>
        <p className="text-red-600">Virhe: {error}</p>
      </section>
    );
  }

  // --- Suodatinrivi (kategoriat + haku) ---
  const onCategoryChange = (e) => {
    setCategoryId(e.target.value);
    setPage(1);
  };
  const onSubmitSearch = (e) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get("q")?.toString() || "";
    setSearch(q.trim());
    setPage(1);
  };

  // Hero-tekstin alta näkyvä dynaaminen teksti (haku/kategoria)
  // const heroSubtitle = search
  //   ? `Hakutulokset: “${search}”`
  //   : categoryId
  //   ? `Kategoria: ${cats.find((c) => String(c.id) === String(categoryId))?.name ?? ""}`
  //   : "Vinkkejä juhliin, hinnoitteludataa ja tarinoita keikoilta";

  return (
    <section className="max-w-6xl mx-auto px-4 pt-24 pb-24 relative">
      {/* [SPINNER] Päivitys-overlay näkyy vain myöhemmissä hauissa/paginoinneissa */}
      {loading && <PageOverlayLoading label="Päivitetään artikkeleita…" />}

      <Helmet onChangeClientState={helmetCb}>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* Hero */}
      <header
        className={`relative overflow-hidden rounded-2xl mt-6 mb-8 ring-1 ${panelTone} h-[42vh] min-h-[320px]`}
      >
        {/* Taustakuva/parallax tai fallback-glow */}
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          {heroFeat?.url ? (
            <img
              src={heroFeat.url}
              alt=""
              className="h-full w-full object-cover"
              loading="eager"
              decoding="async"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(120% 100% at 50% 0%, rgba(255,45,149,0.20) 0%, transparent 60%)," +
                  "radial-gradient(120% 100% at 50% 60%, rgba(0,184,255,0.20) 0%, transparent 60%)," +
                  "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)",
              }}
              aria-hidden
            />
          )}
        </motion.div>

        {/* Kiilto + overlay */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,255,255,0.08) 15%, transparent 70%)," +
              "linear-gradient(300deg, rgba(255,255,255,0.06) 10%, transparent 80%)",
            maskImage:
              "radial-gradient(120% 100% at 50% 60%, black 55%, transparent 25%)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.0, 1, 0.65] }}
          transition={{ duration: 2.0, ease: "easeOut" }}
          aria-hidden
        />

        {/* Kiinteä scrim */}
        <div
          className={`absolute inset-0 ${
            theme === "dark" ? "bg-black/25" : "bg-black/40"
          }`}
          aria-hidden
        />

        {/* Pohjascrim */}
        <div
          className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/70 via-black/40 to-transparent"
          aria-hidden
        />

        <motion.div
          className={`absolute inset-0 bg-gradient-to-b ${heroOverlay}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          aria-hidden
        />

        {/* Tekstit + uusin artikkeli */}
        <div className="relative z-10 flex h-full items-center">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <motion.h1
              className="mt-3 font-customFont2 dark:text-shadow-lg text-center text-electric dark:text-primary xxxsm:text-2xl xxsm:text-4xl md:text-6xl font-bold leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]"
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
            >
              Buukkerin blogi
            </motion.h1>

            {/* <motion.p
              className="mt-3 dark:text-white/100 text-electric/100 font-customFont1 xxxsm:text-sm text-lg text-center text-shadow-lg font-bold drop-shadow-[0_1px_6px_rgba(0,0,0,0.55)]"
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.35, ease: "easeOut", delay: 0.08 }}
            >
              {heroSubtitle}
            </motion.p> */}

            {/* Uusin artikkeli -nosto (vain kun ei ole hakua/kategoriafiltteriä) */}
            {latestPost && !search && !categoryId && (
              <motion.div
                className="mt-5 max-w-2xl mx-auto rounded-2xl bg-black/55 backdrop-blur-md px-4 py-3 sm:px-5 sm:py-4 border border-white/10"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
              >
                <p className="text-xs uppercase tracking-wide text-white/60 mb-1">
                  Uusin artikkeli
                </p>
                <h2
                  className="text-base sm:text-lg md:text-xl font-semibold text-white mb-1 leading-snug"
                  dangerouslySetInnerHTML={{ __html: latestPost.title.rendered }}
                />
                <p className="text-xs sm:text-sm text-white/80 line-clamp-3">
                  {stripHtml(latestPost.excerpt?.rendered || "")}
                </p>
                <div className="mt-3 flex justify-end">
                  <Link
                    to={`/blogi/${latestPost.slug}`}
                    aria-label="Lue uusin artikkeli"
                  >
                    <NeonButton
                      variant="secondary"
                      size="sm"
                      className="font-semibold"
                    >
                      Lue artikkeli
                    </NeonButton>
                  </Link>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </header>

      {/* Lyhyt intro blogista */}
      <p className="mb-8 text-black/80 dark:text-white/80 text-sm md:text-base max-w-3xl">
        Buukkerin blogissa julkaistaan oppaita esiintyjän buukkaukseen, vinkkejä
        esiintyjille ja tapahtumajärjestäjille sekä juttuja Buukkerin
        toiminnasta. Artikkeleissa hyödynnetään samaa hinnoitteludataa, jota
        käytetään palvelussa, kun asiakas buukkaa esiintyjän.
      </p>

      {/* GRID: sisältö + sivupalkki */}
      <div className="grid grid-cols-1 lg:grid-cols-12 xl:grid-cols-12 2xl:grid-cols-12 gap-8">
        {/* Pääkolumni */}
        <div
          className="col-span-8 lg:col-span-8 xl:col-span-8 2xl:col-span-8 order-1 min-w-0"
          aria-busy={loading}
        >
          {/* Suodattimet */}
          <div className="mb-8 flex flex-col gap-3 sm:flex-row lg:items-center">
            <form onSubmit={onSubmitSearch} className="flex-1 flex gap-2">
              <input
                name="q"
                defaultValue={search}
                placeholder="Hae artikkeleita…"
                className="w-full rounded-xl border border-black/10 dark:border-white/10
                           bg-white/60 dark:bg-zinc-900/60 px-4 py-2 font-customFont1 text-xs md:text-lg"
                disabled={loading} // [SPINNER]
              />
              <NeonButton
                type="submit"
                variant="primary"
                className="scale-[0.80] md:scale-100 font-semibold"
                size="sm"
                IconLeft={Search}
                aria-label="Hae artikkeleita"
                disabled={loading} // [SPINNER]
              >
                {loading ? "Hetki…" : "Hae"} {/* [SPINNER] */}
              </NeonButton>
            </form>

            <div className="md:w-72 flex items-center gap-2">
              <select
                disabled={filterLoading || loading} // [SPINNER]
                value={categoryId}
                onChange={onCategoryChange}
                className="w-full rounded-xl border border-black/10 dark:border-white/10
                           bg-white/70 dark:bg-zinc-900/60 px-3 py-2
                           text-black/60 dark:text-white/60 font-customFont1 text-xs md:text-lg"
              >
                <option value="">Valitse kategoria..</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {filterLoading && <Spinner size="sm" showLabel={false} />}{" "}
              {/* [SPINNER] */}
            </div>
          </div>

          {/* Tyhjä hakutulos */}
          {!posts.length ? (
            <p>Ei artikkeleita.</p>
          ) : (
            <>
              {/* Kortit */}
              <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                {posts.map((post, idx) => {
                  const feat = extractFeatured(post);
                  const primaryCat =
                    post?._embedded?.["wp:term"]?.[0]?.[0] || null;
                  const isFeatured =
                    post?._embedded?.["wp:term"]?.[0]?.some(
                      (cat) =>
                        cat.slug === "nosto" ||
                        cat.slug === "featured" ||
                        cat.slug === "hintaraportti-2025"
                    ) || false;

                  return (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.25,
                        delay: 0.03 * idx,
                      }}
                      className="rounded-2xl overflow-hidden bg-white/70 dark:bg-zinc-900/60
                                 shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <Link
                        to={`/blogi/${post.slug}`}
                        aria-label={post.title.rendered}
                      >
                        <div className="relative aspect-[16/9] overflow-hidden">
                          {isFeatured && (
                            <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-pink-600 text-white text-[11px] font-semibold shadow-md">
                              Nosto
                            </div>
                          )}
                          <ImageWithFallback
                            src={feat.url}
                            srcSet={feat.srcSet}
                            sizes={feat.sizes}
                            alt={feat.alt || ""}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-5 md:p-6">
                          {primaryCat && (
                            <span className="inline-flex items-center px-2 py-0.5 mb-2 rounded-full text-[11px] uppercase tracking-wide bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/70">
                              {primaryCat.name}
                            </span>
                          )}
                          <h2
                            className="mb-2 leading-snug font-semibold
                                       text-black/80 dark:text-white/80 font-customFont1 text-lg"
                            dangerouslySetInnerHTML={{
                              __html: post.title.rendered,
                            }}
                          />
                          <p className="text-black/80 dark:text-lemon/80 font-customFont1 text-xs md:text-sm mb-3">
                            {(post?._embedded?.author?.[0]?.name ||
                              "Tuntematon kirjoittaja")}{" "}
                            •{" "}
                            {new Date(
                              post.date
                            ).toLocaleDateString("fi-FI")}
                          </p>
                          <p className="text-zinc-700 dark:text-zinc-300 line-clamp-3 md:line-clamp-4">
                            {stripHtml(post.excerpt?.rendered)}
                          </p>
                          <div className="mt-4 inline-flex items-center gap-2 font-medium
                                          text-black/80 dark:text-white/80 font-customFont1 text-xs md:text-lg">
                            Lue lisää <span aria-hidden>→</span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  );
                })}
              </div>

              {/* Sivutus */}
              <nav
                className="mt-10 flex items-center justify-center gap-3"
                aria-label="Sivutus"
              >
                <NeonButton
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.max(1, p - 1))
                  }
                  disabled={page <= 1 || loading} // [SPINNER]
                  variant="primary"
                  className="scale-[0.80] md:scale-100 font-semibold"
                  size="sm"
                  IconLeft={ChevronLeft}
                  aria-label="Edellinen sivu"
                  title="Edellinen sivu"
                >
                  Edellinen
                </NeonButton>

                <span
                  className="px-6 py-1 text-center rounded-xl bg-electric/80 dark:bg-electric text-xs md:text-lg font-medium"
                  aria-current="page"
                >
                  Sivu {page} / {totalPages}
                </span>

                <NeonButton
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={page >= totalPages || loading} // [SPINNER]
                  variant="primary"
                  className="scale-[0.80] md:scale-100 font-semibold"
                  size="sm"
                  IconRight={ChevronRight}
                  aria-label="Seuraava sivu"
                  title="Seuraava sivu"
                >
                  Seuraava
                </NeonButton>
              </nav>
            </>
          )}
        </div>

        {/* Sivupalkki */}
        <div
          className="
          xxxsm:col-span-8 xxsm:col-span-8 s24:col-span-8 xsm:col-span-8 sm:col-span-8 md:col-span-8 mdd:col-span-8
          lg:col-span-4 xl:col-span-4 2xl:col-span-4
          order-1 lg:order-2 xl:order-2 2xl:order-2"
        >
          <BlogSidebar
            cats={cats}
            onPickCategory={(id) => {
              setCategoryId(id);
              setPage(1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            recent={posts.slice(0, 5)}
            tags={[]}
          />
        </div>
      </div>
    </section>
  );
}
